import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/apiService';

const LoginContainer = styled.div`
  min-height: 100vh;
  background-image: url('/farm-background.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 120px;
  
  /* Remove purple scrollbar */
  scrollbar-color: #1e293b #f0f0f0;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #1e293b;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #0f172a;
  }
  
  @media (max-width: 768px) {
    padding-bottom: 150px;
  }
  
  @media (max-width: 480px) {
    padding-bottom: 160px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    pointer-events: none;
    z-index: 1;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 90%;
  max-width: 1000px;
  gap: 3rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    max-width: 95%;
  }
`;

const BrandSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  color: white;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const BrandTitle = styled.h1`
  display: block;
  font-size: 3rem;
  font-weight: 600;
  margin: 0 0 2rem 0;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 968px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const LogoContainer = styled.div`
  width: 240px;
  height: 240px;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
  border: none;
  
  @media (max-width: 768px) {
    width: 180px;
    height: 180px;
    margin-bottom: 0.8rem;
  }
`;

const LogoContent = styled.div`
  text-align: center;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  height: 100%;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
  
  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
  }
`;

const BrandSubtitle = styled.p`
  display: block;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 2rem 0 0 0;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.15);
  letter-spacing: 0.3px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const LoginCard = styled.div`
  flex: 0 1 auto;
  background: transparent;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: none;
  backdrop-filter: none;
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 480px;
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 18px;
    width: 100%;
    max-width: 420px;
  }
`;

const CardTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  color: white;
  margin: 0 0 1.5rem 0;
  font-weight: 700;
  letter-spacing: 0.3px;
  position: relative;
  display: inline-block;
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  
  label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
  
  input {
    padding: 0.85rem 1.15rem;
    border: 1.5px solid rgba(255, 255, 255, 0.7);
    border-radius: 6px;
    font-size: 0.95rem;
    transition: all 0.2s ease;
    background: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-weight: 500;
    color: #000000;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    
    &::placeholder {
      color: #aaa;
      font-weight: 400;
    }
    
    &:focus {
      outline: none;
      border-color: white;
      background: white;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      transform: translateY(-1px);
    }
    
    &:disabled {
      background: #f0f0f0;
      cursor: not-allowed;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #7f1d1d;
  padding: 1rem 1.2rem;
  border-radius: 6px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 500;
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.08);
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '⚠️';
    margin-right: 0.5rem;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: white;
  color: #000000;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-top: 1.5rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &:hover:not(:disabled) {
    background: #fafafa;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    background: #e5e5e5;
    color: #999;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const ForgotPassword = styled.a`
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 0.8rem;
  transition: all 0.2s ease;
  cursor: pointer;
  display: inline-block;
  width: 100%;
  padding: 0.4rem 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: 0.2px;
  
  &:hover {
    color: white;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0 1.3rem 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const RegistrationSection = styled.div`
  text-align: center;
  margin-top: 1.3rem;
  padding-top: 1.3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  
  p {
    margin: 0 0 1rem 0;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    font-size: 0.8rem;
    letter-spacing: 0.2px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;

const RegistrationButtonGroup = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const RegistrationButton = styled.button`
  padding: 0.7rem 1.4rem;
  background: rgba(255, 255, 255, 0.92);
  color: #1e293b;
  border: 1px solid white;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 255, 255, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.2px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.55);
  padding: 12px 0;
  backdrop-filter: blur(2px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔐 [Login] Form submitted');
    
    if (!phoneNumber.trim() || !password.trim()) {
      console.warn('⚠️ [Login] Validation failed - missing phone or password');
      setError('Phone number and password are required');
      return;
    }

    try {
      console.log('🔐 [Login] Starting login attempt with phone:', phoneNumber);
      setError('');
      setLoading(true);
      
      console.log('🔐 [Login] Calling authService.login...');
      const response = await authService.login(phoneNumber, password);
      
      console.log('✅ [Login] Raw API response:', response);
      console.log('✅ [Login] Response keys:', Object.keys(response));
      
      // Handle both camelCase and PascalCase property names from backend
      const token = response.token || response.Token;
      const role = response.role || response.Role;
      const userId = response.userId || response.UserId;
      
      if (!token) {
        console.error('❌ [Login] Invalid response - missing token');
        throw new Error('Invalid response from server - missing token');
      }

      console.log('✅ [Login] Token received, extracting role:', role);
      
      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      localStorage.setItem('userRole', role); // For Messaging component
      localStorage.setItem('userId', userId);
      localStorage.setItem('phoneNumber', phoneNumber);
      localStorage.setItem('userName', role === 'Admin' ? 'Admin' : `${role} - ${userId}`);

      // Set buyerId/sellerId for messaging - fetch profile to get actual buyer/seller ID
      try {
        const profile = await authService.getUserProfile(response.userId);
        console.log('✅ [Login] Profile fetched:', profile);
        
        if (response.role === 'Buyer' && profile.buyerProfile?.buyerId) {
          localStorage.setItem('buyerId', profile.buyerProfile.buyerId);
          console.log('✅ [Login] Stored buyerId:', profile.buyerProfile.buyerId);
        } else if (response.role === 'Seller' && profile.sellerProfile?.sellerId) {
          localStorage.setItem('sellerId', profile.sellerProfile.sellerId);
          console.log('✅ [Login] Stored sellerId:', profile.sellerProfile.sellerId);
        }
      } catch (profileErr) {
        console.warn('⚠️ [Login] Could not fetch profile, using userId:', response.userId);
        // Fallback: use userId as both buyerId and sellerId for now
        localStorage.setItem('buyerId', response.userId);
        localStorage.setItem('sellerId', response.userId);
      }

      console.log('✅ [Login] LocalStorage updated, calling onLoginSuccess');
      onLoginSuccess(response);
      console.log('✅ [Login] onLoginSuccess called successfully');
      
      console.log('🔐 [Login] Navigating to dashboard...');
      setTimeout(() => navigate('/'), 100);
    } catch (err) {
      console.error('❌ [Login] Error during login:', err);
      console.error('❌ [Login] Error stack:', err.stack);
      setError(err.message || 'Invalid phone number or password. Please try again.');
      setPassword('');
    } finally {
      console.log('🔐 [Login] Finishing login attempt');
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <ContentWrapper>
        <BrandSection>
          <BrandTitle>Buyer Seller Platform</BrandTitle>
          <LogoContainer>
            <LogoContent>
              <LogoImage src="/my-mango-logo.png?v=2" alt="Mango Logo" />
            </LogoContent>
          </LogoContainer>
          <BrandSubtitle>Mango Growers Association, Dharwad</BrandSubtitle>
        </BrandSection>

        <LoginCard>
          <CardTitle>Welcome!</CardTitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <LoginForm onSubmit={handleSubmit}>
            <FormGroup>
              <label>USERNAME</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
                required
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <label>PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={loading}>
              <span>{loading ? '⏳ Logging in...' : 'LOGIN'}</span>
            </SubmitButton>

            <ForgotPassword href="#">Forgot Password</ForgotPassword>
          </LoginForm>

          <Divider>Don't have an account?</Divider>

          <RegistrationSection>
            <p>Register as a new farmer or buyer to get started</p>
            <RegistrationButtonGroup>
              <RegistrationButton type="button" onClick={() => navigate('/register-seller')}>
                <span>🌾 Seller</span>
              </RegistrationButton>
              <RegistrationButton type="button" onClick={() => navigate('/register-buyer')}>
                <span>🛒 Buyer</span>
              </RegistrationButton>
            </RegistrationButtonGroup>
          </RegistrationSection>
        </LoginCard>
      </ContentWrapper>
      <Footer>
        © 2026 Neu Edge Software Private Limited. All rights reserved.
      </Footer>
    </LoginContainer>
  );
};

export default Login;
